import {
  getTicketSaleKey,
  getTicketSalesKey,
  useOpenTicketTicketSale,
} from 'api-hooks/sale/ticket-sale';
import formSetErrors from 'common/helpers/form-setError';
import { queryClient } from 'common/repositories/query-client';
import { themedStyled } from 'common/styles/styletron.theme';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import { FullContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

type FormType = {
  isOpenTicket: boolean;
  lastReissueDate: Date | null;
  lastTravellingDate: Date | null;
  ticketDescription: string;
};

interface Props {
  refId: string;
  onClose: () => void;
}

const yupSchema = Yup.object()
  .shape({
    lastReissueDate: Yup.date().nullable(),
    lastTravellingDate: Yup.date().nullable(),
    ticketDescription: Yup.string().required(),
  })
  .required();

export default function OpenTicketPanel(props: Props) {
  const { t } = useTranslation();
  const { refId, onClose } = props;

  const { mutateAsync } = useOpenTicketTicketSale();

  const defaultValues = React.useMemo<FormType>(
    () => ({
      isOpenTicket: true,
      lastReissueDate: new Date(),
      lastTravellingDate: new Date(),
      ticketDescription: '',
    }),
    [],
  );

  const methods = useForm<FormType>({
    defaultValues,
    resolver: useYupValidationResolver(yupSchema),
    mode: 'onChange',
  });

  const onSubmit = async (values: FormType) => {
    try {
      const result = await mutateAsync({
        flightSaleRefId: refId,
        body: values,
      });
      notification.success({ message: result?.message });

      queryClient.refetchQueries([getTicketSalesKey()[0]]);
      queryClient.refetchQueries([getTicketSaleKey()[0]]);
      props.onClose();
    } catch (e) {
      if (e.errors) {
        formSetErrors(e.errors, methods.setError);
      }
      notification.error({ message: e.message });
    }
  };

  return (
    <>
      <DrawerHeader
        onClose={onClose}
        title={t('ticketSales:set_open_ticket')}
      />
      <DrawerBody>
        <Form methods={methods} onSubmit={onSubmit}>
          <FullContainer
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <HalfContainer>
              <input
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                style={{
                  width: 0,
                  height: 0,
                  outline: 'none',
                  border: 'none',
                  position: 'absolute',
                }}
              />
              <Input
                type="date-time"
                name="lastReissueDate"
                label={t('ticketSales:last_reissue_date')}
              />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="date-time"
                name="lastTravellingDate"
                label={t('ticketSales:last_travelling_date')}
              />
            </HalfContainer>
          </FullContainer>
          <FullContainer>
            <Input
              type="text"
              name="ticketDescription"
              label={t('common:description')}
              required
            />
          </FullContainer>
          <FullContainer style={{ display: 'flex', justifyContent: 'end' }}>
            <Button buttonType="tertiary" onClick={props.onClose}>
              {t('common:cancel')}
            </Button>
            <Separator gap={8} />
            <Input type="submit" />
          </FullContainer>
        </Form>
      </DrawerBody>
    </>
  );
}

const HalfContainer = themedStyled('div', ({ $theme }) => ({
  width: '100%',
  [`${$theme.mediaQuery.medium}`]: {
    width: '47%',
  },
}));
